import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TableSkeleton } from '@components/medicus-ui/Loaders';

/** Opciones de filas por página. El default (10) es el primero. */
const FILAS_POR_PAGINA = [10, 25, 50] as const;
type FilasPorPagina = (typeof FILAS_POR_PAGINA)[number];

export interface PaginaAltas<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

interface Props<T> {
  /** Clave base de react-query; se le agregan página y tamaño. */
  queryKey: string;
  fetcher: (page: number, pageSize: number) => Promise<PaginaAltas<T>>;
  columnas: string[];
  /** Celdas de una fila, en el mismo orden que `columnas`. */
  fila: (item: T) => ReactNode[];
  /** Clave estable para el `key` de React. */
  claveFila: (item: T) => string;
  vacio: string;
}

/**
 * Tabla "Últimas altas": historial de lo que se despachó a Binary por API desde
 * la pantalla, más reciente primero. Paginada y sin búsqueda a propósito — es un
 * registro de lo último que pasó, no un buscador (para eso está la vista
 * Búsqueda). El tamaño de página arranca en 10 y se puede subir a 25 o 50.
 */
export function UltimasAltas<T>({
  queryKey,
  fetcher,
  columnas,
  fila,
  claveFila,
  vacio,
}: Props<T>) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<FilasPorPagina>(FILAS_POR_PAGINA[0]);

  const query = useQuery({
    queryKey: [queryKey, page, pageSize],
    queryFn: () => fetcher(page, pageSize),
    // Mantiene la tabla anterior visible mientras trae la página nueva, así no
    // parpadea a skeleton en cada click de paginado.
    placeholderData: (prev) => prev,
  });

  const data = query.data;
  const total = data?.total ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(total / pageSize));
  const desde = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const hasta = Math.min(page * pageSize, total);

  function cambiarTamanio(n: FilasPorPagina) {
    setPageSize(n);
    // Volver a la primera página: la actual puede no existir con el tamaño nuevo.
    setPage(1);
  }

  return (
    <div className="card" style={{ marginTop: 24 }}>
      <div className="rowBetween" style={{ alignItems: 'center' }}>
        <h2 className="heading-5">Últimas altas</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="text-element-mid" style={{ fontSize: 12 }}>
            Filas
          </span>
          {FILAS_POR_PAGINA.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => cambiarTamanio(n)}
              aria-pressed={pageSize === n}
              className={pageSize === n ? 'btn btn-primary btn--sm' : 'btn btn-ghost btn--sm'}
              style={{ minWidth: 40 }}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      {query.isLoading && (
        <div style={{ marginTop: 12 }}>
          <TableSkeleton rows={4} />
        </div>
      )}

      {query.isError && (
        <p className="status status-error" style={{ marginTop: 12, display: 'block' }}>
          No se pudieron cargar las últimas altas.
        </p>
      )}

      {data && data.items.length === 0 && (
        <p className="text-element-mid" style={{ marginTop: 12, fontSize: 13 }}>
          {vacio}
        </p>
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="tableWrap" style={{ marginTop: 12, overflowX: 'auto' }}>
            <table className="dataTable">
              <thead>
                <tr>
                  {columnas.map((c) => (
                    <th key={c}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={claveFila(item)}>
                    {fila(item).map((celda, i) => (
                      <td key={columnas[i]}>{celda}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <footer
            className="rowBetween"
            style={{
              alignItems: 'center',
              marginTop: 16,
              paddingTop: 12,
              borderTop: '1px solid var(--color-border, #E2E8F0)',
            }}
          >
            <span className="text-element-mid" style={{ fontSize: 12 }}>
              Mostrando {desde}-{hasta} de {total} · Página {page} de {totalPaginas}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn btn-ghost btn--sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </button>
              <button
                type="button"
                className="btn btn-ghost btn--sm"
                disabled={page >= totalPaginas}
                onClick={() => setPage((p) => Math.min(totalPaginas, p + 1))}
              >
                Siguiente
              </button>
            </div>
          </footer>
        </>
      )}
    </div>
  );
}

/**
 * Estado del afiliado en Binary. Binary es la fuente de verdad: Mongo sólo
 * guarda cómo salió el alta al despacharla, y desde entonces la persona pudo
 * haberse dado de baja. Un guion = Binary no respondió (no es que esté mal).
 */
export function EstadoBinary({ estado }: { estado?: string | null }) {
  if (!estado) return <span className="text-element-mid">—</span>;
  const activo = estado === 'A';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        color: activo ? '#166534' : '#9B1C1C',
        background: activo ? '#E2F3E8' : '#FDE2E2',
      }}
    >
      {activo ? 'Activo' : estado === 'B' ? 'Baja' : estado}
    </span>
  );
}
